import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Cell, CellText, Drop, Link } from '@hh.ru/magritte-ui';
import { CheckOutlinedSize16, ChevronUpDownOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import urlParser from 'bloko/common/urlParser';

import { ClustersSetting } from 'src/components/Search/Common/Filters/utils';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { WITH_TOP_FILTER_CATALOG } from 'src/models/topFilterCatalog';
import { OrderByOption } from 'src/types/search/common/criteria';

const TrlKeys = {
    [OrderByOption.PublicationTime]: 'employer.settings.searchpublication.sort.date',
    [OrderByOption.SalaryDesc]: 'search.order.salary_desc',
    [OrderByOption.SalaryAsc]: 'search.order.salary_asc',
    [OrderByOption.Relevance]: 'search.order.relevance',
};

const searchLoadingAction = makeSetStoreField('searchLoading');

const SearchOrder: TranslatedComponent = ({ trls }) => {
    const dispatch = useDispatch();
    const push = usePush();
    const { pathname, query: routerLocationQuery } = useSelector((state) => state.router.location);
    const query = useSelector((state) => state.vacancySearchResult.queries.orderByBase);
    const value = useSelector((state) => {
        const value = state.vacancySearchResult.criteria?.order_by;
        return Object.values(OrderByOption).includes(value) ? value : OrderByOption.Relevance;
    });
    const options = useSelector((state) => state.vacancySearchDictionaries.orderBy);
    const enableSimilarSavedSearch = useSelector((state) => state.vacancySearchResult.enableSimilarSavedSearch);

    const [isDropVisible, setDropVisible] = useState(false);
    const selectorHost = useRef(null);

    const handleChange = useCallback(
        (value: OrderByOption) => {
            const orderUrl = urlParser(
                `${pathname}?${query || ''}&order_by=${value || ''}${
                    enableSimilarSavedSearch ? `&${ClustersSetting.ForceFiltersSaving}=true` : ''
                }`
            );
            if (WITH_TOP_FILTER_CATALOG in routerLocationQuery) {
                orderUrl.params[WITH_TOP_FILTER_CATALOG] = routerLocationQuery[WITH_TOP_FILTER_CATALOG];
            }
            dispatch(searchLoadingAction(true));
            push(orderUrl.href);
        },
        [pathname, query, enableSimilarSavedSearch, routerLocationQuery, dispatch, push]
    );

    if (!options) {
        return null;
    }

    return (
        <>
            <Drop
                visible={isDropVisible}
                activatorRef={selectorHost}
                onClose={() => setDropVisible(false)}
                placement="bottom-left"
                forcePlacement
                data-qa="order-by-menu-drop"
            >
                {options.map((option) => (
                    <Cell
                        key={option}
                        vertPadding
                        horPadding
                        roundedBorder
                        onClick={() => {
                            setDropVisible(false);
                            if (option !== value) {
                                handleChange(option);
                            }
                        }}
                        data-qa={`order_by-${option}`}
                        right={value === option ? <CheckOutlinedSize16 initialColor="accent" /> : undefined}
                    >
                        <CellText>{trls[TrlKeys[option]]}</CellText>
                    </Cell>
                ))}
            </Drop>
            <Link
                onClick={() => setDropVisible(!isDropVisible)}
                iconRight={<ChevronUpDownOutlinedSize16 />}
                ref={selectorHost}
                data-qa="order-by-menu"
            >
                {trls[TrlKeys[value ?? OrderByOption.Relevance]]}
            </Link>
        </>
    );
};

export default translation(SearchOrder);
